import { crewCellSlotOffset, crewWorldPos } from "../sim/crew";
import { shipCellSize } from "../sim/ship";
import type { Crew, EntityId, World } from "../sim/types";
import type { Renderer } from "./renderer";

const DOT_RADIUS_M = 1.8;
const RING_RADIUS_M = 2.6;
const PULSE_HZ = 0.6;
const PULSE_INTENSITY_MIN = 1.0;
const PULSE_INTENSITY_MAX = 1.5;
/** Bit 32 in tile flags. Keep aligned with tiles.wgsl. */
const FLAG_INTENSITY_MULT = 32;

const HEALTH_LOW_FRAC = 0.33;
const HEALTH_HEALTHY_FRAC = 0.66;

export function pushCrewTiles(
  r: Renderer,
  world: World,
  time: number,
  selectedCrewId: EntityId | null,
): void {
  const pulse01 = 0.5 + 0.5 * Math.sin(time * Math.PI * 2 * PULSE_HZ);
  const intensity = PULSE_INTENSITY_MIN + (PULSE_INTENSITY_MAX - PULSE_INTENSITY_MIN) * pulse01;
  const ringUV = r.atlas.lookup.get("crew_dot_ring");
  const uvGreen = r.atlas.lookup.get("crew_dot_green");
  const uvAmber = r.atlas.lookup.get("crew_dot_amber");
  const uvRed   = r.atlas.lookup.get("crew_dot_red");

  const dotSize = DOT_RADIUS_M * 2;
  const ringSize = RING_RADIUS_M * 2;

  for (const crew of world.crew.values()) {
    const ship = world.ships.get(crew.shipId);
    if (!ship) continue;
    const offset = crew.path.length === 0
      ? crewCellSlotOffset(crew.cellSlot, shipCellSize(ship))
      : undefined;
    const w = crewWorldPos(world, crew, time, offset);

    const bvx = ship.body.vel.x;
    const bvy = ship.body.vel.y;
    const bAng = ship.body.angVel;
    const dx = w.x - ship.body.pos.x;
    const dy = w.y - ship.body.pos.y;
    const velX = bvx - bAng * dy;
    const velY = bvy + bAng * dx;

    if (crew.id === selectedCrewId && ringUV) {
      r.pushTileRaw(
        w.x, w.y,
        ringSize, ringSize,
        0, 0,
        ringUV.u, ringUV.v, ringUV.w, ringUV.h,
        0, 0, 1,
        velX, velY, bAng,
      );
    }

    const frac = Math.max(0, Math.min(1, crew.health / 100));
    const uv = frac < HEALTH_LOW_FRAC ? uvRed
      : frac < HEALTH_HEALTHY_FRAC ? uvAmber
      : uvGreen;
    if (!uv) continue;
    r.pushTileRaw(
      w.x, w.y,
      dotSize, dotSize,
      0, FLAG_INTENSITY_MULT,
      uv.u, uv.v, uv.w, uv.h,
      0, 0, intensity,
      velX, velY, bAng,
    );
  }
}

export function hitTestCrew(
  world: World,
  wx: number,
  wy: number,
  time: number,
  radiusM = DOT_RADIUS_M * 1.5,
): Crew | null {
  let best: Crew | null = null;
  let bestD2 = radiusM * radiusM;
  for (const crew of world.crew.values()) {
    const ship = world.ships.get(crew.shipId);
    if (!ship) continue;
    const offset = crew.path.length === 0
      ? crewCellSlotOffset(crew.cellSlot, shipCellSize(ship))
      : undefined;
    const w = crewWorldPos(world, crew, time, offset);
    const dx = w.x - wx, dy = w.y - wy;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) { bestD2 = d2; best = crew; }
  }
  return best;
}
